// Globals
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { forgotPassword } from '../../feature/actionCreators';
import { selectAuthIsLoading } from '../../feature/selectors';

// Components
import { showSuccessToast, useForm } from '@/components';
import ForgotPasswordModal from '../../components/ForgotPasswordModal/ForgotPasswordModal';

// Helpers
import { TForgotPasswordFormValues } from '../../helpers/types';
import ForgotPasswordDTO from '../../dtos/ForgotPasswordDTO';

type TForgotPasswordModalContainer = {
  isOpened: boolean;
  onClose: () => void;
};

const ForgotPasswordModalContainer = ({ isOpened, onClose }: TForgotPasswordModalContainer) => {
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const isLoading = useAppState(selectAuthIsLoading);
  const { t } = useTranslation(['auth', 'common']);

  const handleSubmit = useCallback(
    async (values: TForgotPasswordFormValues) => {
      const forgotPasswordDto = new ForgotPasswordDTO({
        email: values?.email as string,
      });

      try {
        await dispatch(forgotPassword(forgotPasswordDto)).unwrap();
        form.resetFields();
        showSuccessToast({ message: t('auth_set_password_request_message') });
        onClose();
      } catch {}
    },
    [dispatch, form, onClose],
  );

  const handleClose = useCallback(async () => {
    form.resetFields();
    onClose();
  }, [form, onClose]);

  return (
    <ForgotPasswordModal
      isOpened={isOpened}
      onClose={handleClose}
      formInstance={form}
      handleSubmit={handleSubmit}
      isLoading={isLoading}
    />
  );
};

export default ForgotPasswordModalContainer;
